import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { getRecentPolicies } from '../../lib/requests.es6'
import { PolicyLink } from '../Collections/PolicyLink.es6'
import {
  ALWAYS_SHOWN,
  DATE_BY,
  ORDERS,
  PAGE_SIZES,
  PRODUCT_CODE,
  STATUS_CARDS,
  attentionBuckets,
  buildQuery,
  cutPage,
  money,
  pageWindow,
  policyRow,
  share,
  statusMeta
} from './helpers.es6'

const totalOf = response => Number(response && response.data && response.data.total) || 0

// Where every Emple policy stands, and a list behind each number. The cards are counts from the same policy list the
// table uses (one policy asked for, only the total read), so a card and the list it opens always agree.
export default function PoliciesOverview() {
  const [counts, setCounts] = useState({ state: 'loading', all: 0, byStatus: {}, buckets: {} })
  const [status, setStatus] = useState('')
  const [bucketKey, setBucketKey] = useState('')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [dateBy, setDateBy] = useState('created_at')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const [order, setOrder] = useState('newest')
  const [list, setList] = useState({ state: 'loading', rows: [], total: 0, error: '' })

  const buckets = useMemo(() => attentionBuckets(), [])
  const bucket = buckets.filter(b => b.key === bucketKey)[0] || null

  const loadCounts = useCallback(() => {
    setCounts(c => ({ ...c, state: 'loading' }))
    const count = params => getRecentPolicies({ product_code: PRODUCT_CODE, ...params, page: 1, size: 1 }).then(totalOf)
    Promise.all([
      count({}),
      ...STATUS_CARDS.map(s => count({ status: s.key })),
      ...buckets.map(b => count(b.params))
    ]).then(
      values => {
        const byStatus = {}
        STATUS_CARDS.forEach((s, i) => {
          byStatus[s.key] = values[i + 1]
        })
        const byBucket = {}
        buckets.forEach((b, i) => {
          byBucket[b.key] = values[STATUS_CARDS.length + 1 + i]
        })
        setCounts({ state: 'done', all: values[0], byStatus, buckets: byBucket })
      },
      () => setCounts(c => ({ ...c, state: 'error' }))
    )
  }, [buckets])

  useEffect(() => {
    loadCounts()
  }, [loadCounts])

  useEffect(() => {
    let live = true
    setList(l => ({ ...l, state: 'loading', error: '' }))
    const ask = n => getRecentPolicies(buildQuery({ status, bucket, search, dateBy, from, to, page: n, size }))
    // Newest first is how the service answers. Oldest first reads the same list from the far end.
    const load =
      order === 'oldest'
        ? getRecentPolicies(buildQuery({ status, bucket, search, dateBy, from, to, page: 1, size: 1 })).then(first => {
            const total = totalOf(first)
            const window = pageWindow({ total, page, size, order })
            return Promise.all(window.pages.map(n => ask(n))).then(responses => ({
              total,
              data: cutPage(responses.map(r => (r.data && r.data.data) || []), window)
            }))
          })
        : ask(page + 1).then(response => ({ total: totalOf(response), data: (response.data && response.data.data) || [] }))
    load.then(
      body => live && setList({ state: 'done', rows: body.data.map(policyRow), total: body.total, error: '' }),
      () => live && setList({ state: 'error', rows: [], total: 0, error: 'The policies could not be loaded. Try again in a moment.' })
    )
    return () => {
      live = false
    }
  }, [status, bucket, search, dateBy, from, to, page, size, order])

  const pickStatus = key => {
    setStatus(key)
    setBucketKey('')
    setPage(0)
  }
  const pickBucket = key => {
    setBucketKey(key === bucketKey ? '' : key)
    setStatus('')
    setPage(0)
  }
  const clearFilters = () => {
    setSearch('')
    setSearchInput('')
    setFrom('')
    setTo('')
    setDateBy('created_at')
    setPage(0)
  }

  const visibleCards = STATUS_CARDS.filter(s => ALWAYS_SHOWN.indexOf(s.key) !== -1 || counts.byStatus[s.key] > 0 || status === s.key)
  const loading = counts.state === 'loading'
  const selectedLabel = bucket ? bucket.label : status ? statusMeta(status).label : 'All policies'
  const filtering = Boolean(search || from || to)

  const card = (key, label, hint, value, color, selected) => (
    <Paper
      key={key || 'all'}
      variant="outlined"
      role="button"
      tabIndex={0}
      onClick={() => pickStatus(key)}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && pickStatus(key)}
      sx={{
        p: 1.5,
        flex: '1 1 170px',
        minWidth: 170,
        cursor: 'pointer',
        borderColor: selected ? 'primary.main' : undefined,
        borderWidth: selected ? 2 : 1,
        '&:hover': { borderColor: 'primary.main' }
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ lineHeight: 1.2 }}>
        {loading ? '…' : Number(value).toLocaleString()}
      </Typography>
      {key ? (
        <LinearProgress variant="determinate" color={color === 'default' ? 'inherit' : color} value={loading ? 0 : share(value, counts.all)} sx={{ my: 0.5, height: 5, borderRadius: 3 }} />
      ) : null}
      <Typography variant="caption" color="text.secondary">
        {key ? (loading ? '' : `${share(value, counts.all)}% of all policies · ${hint}`) : hint}
      </Typography>
    </Paper>
  )

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" component="h1">
            Policies
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Where every Emplelife Plus policy stands. Pick a number to see the policies behind it.
          </Typography>
        </Box>
        <Button size="small" onClick={loadCounts} disabled={loading}>
          Refresh numbers
        </Button>
      </Box>

      {counts.state === 'error' ? (
        <Alert severity="warning" sx={{ mt: 2 }} action={<Button color="inherit" size="small" onClick={loadCounts}>Try again</Button>}>
          The totals could not be loaded.
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 2 }}>
        {card('', 'All policies', 'Every Emple policy', counts.all, 'primary', !status && !bucketKey)}
        {visibleCards.map(s => card(s.key, s.label, s.hint, counts.byStatus[s.key] || 0, s.color, status === s.key))}
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <Typography variant="subtitle2" gutterBottom>
          Needs attention
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {buckets.map(b => (
            <Chip
              key={b.key}
              clickable
              color={bucketKey === b.key ? 'primary' : 'default'}
              variant={bucketKey === b.key ? 'filled' : 'outlined'}
              onClick={() => pickBucket(b.key)}
              title={b.hint}
              label={`${b.label}: ${loading ? '…' : Number(counts.buckets[b.key] || 0).toLocaleString()}`}
            />
          ))}
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ mt: 2.5 }}>
        <Box sx={{ p: 1.5, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" sx={{ mr: 'auto' }}>
            {selectedLabel}
            {list.state === 'done' ? ` · ${list.total.toLocaleString()}` : ''}
          </Typography>
          <TextField
            size="small"
            label="Search policy number"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                setSearch(searchInput)
                setPage(0)
              }
            }}
            sx={{ minWidth: 200 }}
          />
          <TextField select size="small" label="Order" value={order} onChange={e => { setOrder(e.target.value); setPage(0) }} sx={{ minWidth: 140 }}>
            {ORDERS.map(o => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Date" value={dateBy} onChange={e => setDateBy(e.target.value)} disabled={Boolean(bucket)} sx={{ minWidth: 120 }}>
            {DATE_BY.map(d => (
              <MenuItem key={d.value} value={d.value}>
                {d.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" type="date" label="From" value={from} disabled={Boolean(bucket)} InputLabelProps={{ shrink: true }} onChange={e => { setFrom(e.target.value); setPage(0) }} />
          <TextField size="small" type="date" label="To" value={to} disabled={Boolean(bucket)} InputLabelProps={{ shrink: true }} onChange={e => { setTo(e.target.value); setPage(0) }} />
          <Button size="small" onClick={() => { setSearch(searchInput); setPage(0) }}>
            Search
          </Button>
          {filtering ? (
            <Button size="small" onClick={clearFilters}>
              Clear filters
            </Button>
          ) : null}
        </Box>

        {list.state === 'loading' ? <LinearProgress /> : null}
        {list.state === 'error' ? (
          <Alert severity="error" sx={{ m: 1.5 }}>
            {list.error}
          </Alert>
        ) : null}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Policy</TableCell>
                <TableCell>Principal member</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Cover</TableCell>
                <TableCell align="right">Premium</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Owing</TableCell>
                <TableCell>Due</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.rows.map(r => {
                const meta = statusMeta(r.status)
                return (
                  <TableRow key={r.guid || r.number} hover>
                    <TableCell>
                      <PolicyLink policyNumber={r.number} fallback="-" />
                    </TableCell>
                    <TableCell>{r.name || '-'}</TableCell>
                    <TableCell>{r.phone || '-'}</TableCell>
                    <TableCell>{r.cover || '-'}</TableCell>
                    <TableCell align="right">{r.premiumCents ? money(r.premiumCents) : '-'}</TableCell>
                    <TableCell>
                      <Chip size="small" color={meta.color} variant={meta.color === 'default' ? 'outlined' : 'filled'} label={meta.label} />
                    </TableCell>
                    <TableCell align="right">{r.owingCents ? money(r.owingCents) : '-'}</TableCell>
                    <TableCell>{r.due || '-'}</TableCell>
                    <TableCell>{r.created || '-'}</TableCell>
                  </TableRow>
                )
              })}
              {list.state === 'done' && !list.rows.length ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No policies match. {filtering ? 'Try clearing the filters.' : ''}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={list.total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={size}
          onRowsPerPageChange={e => {
            setSize(Number(e.target.value))
            setPage(0)
          }}
          rowsPerPageOptions={PAGE_SIZES}
        />
      </Paper>
    </Box>
  )
}
